/**
 * Satış haritası (v0.1.269): GET /sales-map for the chosen period and product family, read when the screen opens, on
 * every switch and on Yenile. The last view of each period and family stays cached, so a switch back shows it at once
 * while it reads again. Read only.
 */

import {
  SALES_MAP_DEFAULT_PERIOD,
  type SalesMapCategoryFilter,
  type SalesMapPeriod,
  type SalesMapView,
} from '@twin/contracts';
import { useSyncExternalStore } from 'react';
import type { TwinApi } from '../api/client.js';
import { useStore } from './context.js';

export interface SalesMapState {
  period: SalesMapPeriod;
  category: SalesMapCategoryFilter;
  /** The last read of each "period:category". */
  views: Readonly<Record<string, SalesMapView>>;
  error: string | null;
  loading: boolean;
}

export const salesMapKey = (period: SalesMapPeriod, category: SalesMapCategoryFilter): string =>
  `${period}:${category}`;

export class SalesMapStore {
  private state: SalesMapState = {
    period: SALES_MAP_DEFAULT_PERIOD,
    category: 'all',
    views: {},
    error: null,
    loading: false,
  };
  private readonly inFlight = new Map<string, Promise<void>>();
  private readonly listeners = new Set<() => void>();

  constructor(private readonly api: TwinApi) {}

  readonly subscribe = (listener: () => void): (() => void) => {
    this.listeners.add(listener);
    return () => this.listeners.delete(listener);
  };

  readonly getState = (): SalesMapState => this.state;

  private set(patch: Partial<SalesMapState>): void {
    this.state = { ...this.state, ...patch };
    for (const listener of this.listeners) listener();
  }

  /** The view of the current choice, null before its first read. */
  current(): SalesMapView | null {
    return this.state.views[salesMapKey(this.state.period, this.state.category)] ?? null;
  }

  /** Reads the current choice; one read per choice at a time. */
  reload(): Promise<void> {
    const { period, category } = this.state;
    const key = salesMapKey(period, category);
    const running = this.inFlight.get(key);
    if (running !== undefined) return running;
    this.set({ loading: true });
    const run = (async () => {
      try {
        const view = await this.api.getSalesMap(period, category);
        const patch: Partial<SalesMapState> = { views: { ...this.state.views, [key]: view } };
        // An answer for a choice Sercan has left fills the cache only.
        if (salesMapKey(this.state.period, this.state.category) === key) {
          Object.assign(patch, { error: null, loading: false });
        }
        this.set(patch);
      } catch (error) {
        if (salesMapKey(this.state.period, this.state.category) === key) {
          this.set({ error: error instanceof Error ? error.message : String(error), loading: false });
        }
      }
    })();
    this.inFlight.set(key, run);
    void run.finally(() => {
      if (this.inFlight.get(key) === run) this.inFlight.delete(key);
    });
    return run;
  }

  setPeriod(period: SalesMapPeriod): Promise<void> {
    if (period !== this.state.period) this.set({ period, error: null, loading: false });
    return this.reload();
  }

  setCategory(category: SalesMapCategoryFilter): Promise<void> {
    if (category !== this.state.category) this.set({ category, error: null, loading: false });
    return this.reload();
  }
}

export function useSalesMap(): { store: SalesMapStore; state: SalesMapState } {
  const store = useStore().salesMap;
  const state = useSyncExternalStore(store.subscribe, store.getState, store.getState);
  return { store, state };
}
